import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import merge from 'lodash.merge';
import mapValues from 'lodash.mapvalues';
import omit from 'lodash.omit';
import Select from '../../../components/Select/Select';
import TextArea from '../../../components/TextArea/TextArea';
import {
  blogForm,
  initialValues as defaultInitialValues,
  ASYNC_FIELDS
} from '../../../schemas/forms/blog.schema';
import Input from '../../../components/Input/Input';
import InputText from '../../../components/Input/InputText';
import { ReactComponent as IconAutoFixHigh } from '../../../public/images/svg/icon_auto_fix_high.svg';
import Button from '../../../components/Button/Button';
import DealershipAccountField from '../../../components/DealershipAccountField/DealershipAccountField';
import { vehicleInfo } from '../../../service/Vehicle.service';
import LoadableSelect from '../../../components/Select/LoadableSelect';
import { handleError } from '../../../service/base.service';
import { CONTENT_TYPE } from '../../../enums';
import Checkbox from '../../../components/Checkbox/Checkbox';
import { digitsOnly } from './helper';

const BlogForm = ({ contentType, handleContentType, onSubmit, initialValues }) => {
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: { ...defaultInitialValues, contentType },
    validationSchema: blogForm,
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: (form) => {
      onSubmit(blogForm.cast(form, { stripUnknown: true }));
    }
  });

  // https://github.com/jaredpalmer/formik/issues/3602#issuecomment-1512896214
  // tl;dr; for some reason if we call formik as dependency inside the effect it goes into an infinite loop
  const { setValues, setTouched, validateForm } = formik;

  const vehicleCatalogYear = useCallback(async () => {
    if (formik.values.contentType !== '') {
      const res = await vehicleInfo();
      return [{ label: 'Select', value: '' }, ...res.data];
    }
  }, [formik.values.contentType]);

  const vehicleCatalogMaker = useCallback(async () => {
    if (formik.values.vehicle.year !== '') {
      const res = await vehicleInfo({ year: formik.values?.vehicle?.year });
      return [{ label: 'Select', value: '' }, ...res.data];
    }
  }, [formik.values.vehicle.year]);

  const vehicleCatalogModel = useCallback(async () => {
    if (formik.values.vehicle.make !== '') {
      const res = await vehicleInfo({
        year: formik.values?.vehicle?.year,
        make: formik.values?.vehicle?.make
      });
      return [{ label: 'Select', value: '' }, ...res.data];
    }
  }, [formik.values.vehicle.make, formik.values.vehicle?.year]);

  const handleOptionsRejectedCallback = (e) => {
    handleError(e, {
      UnauthorizedError: navigate,
      default: () => console.error(e)
    });
  };

  useEffect(() => {
    if (!initialValues) {
      return;
    }

    // make sure that inline object goes first, not to merge into defaultInitialValues directly as it will mutate it
    const formValues = merge({ contentType }, defaultInitialValues, initialValues);
    const touchFields = (v) => (typeof v === 'object' ? mapValues(v, touchFields) : true);
    const touchedFields = touchFields(formValues);

    // don't touch async fields to avoid showing errors before they reach a consistent state
    setValues(formValues)
      .then(() => setTouched(omit(touchedFields, ASYNC_FIELDS)))
      .then(() => validateForm(omit(formValues, ASYNC_FIELDS)));
  }, [initialValues, contentType, setValues, setTouched, validateForm]);

  return (
    <div
      className="flex flex-wrap flex-auto justify-between align-center mt-4 gap-8"
      data-testid="blog-form"
    >
      <div className="w-3/12 max-h-max min-w-[347px] grow bg-white overflow-hidden">
        <div className="bg-gradient-to-r block h-[2px] from-gradient-blue-purple-400 to-gradient-blue-purple-300" />
        <div className="p-6">
          <Select
            id="content-type"
            label="Content Type"
            value={formik.values.contentType}
            onChange={(value) => {
              formik.setFieldValue('contentType', value);
              handleContentType(value);
            }}
            onBlur={formik.handleBlur}
            options={CONTENT_TYPE.toArray()}
            required
            dataTest="blog-form-content-type"
            disabled={!!initialValues}
          />

          <div className="mb-8">
            <p className="block font-sans text-sm font-bold text-gray-600 mb-4">Vehicle</p>
            <LoadableSelect
              label="Year"
              id="vehicle.year"
              value={formik.values.vehicle.year}
              onChange={(value) => {
                formik.setFieldValue('vehicle.year', value);
                formik.setFieldValue('vehicle.make', '');
                formik.setFieldTouched('vehicle.make', false);
                formik.setFieldValue('vehicle.model', '');
                formik.setFieldTouched('vehicle.model', false);
              }}
              onBlur={formik.handleBlur}
              disabled={!formik.values.contentType}
              options={vehicleCatalogYear}
              optionsErrorCallback={handleOptionsRejectedCallback}
              error={formik.touched?.vehicle?.year && formik.errors?.vehicle?.year}
              inline
              searchable
              dataTest="blog-form-vehicle-year"
            />

            <LoadableSelect
              label="Make"
              id="vehicle.make"
              value={formik.values.vehicle.make}
              disabled={!formik.values.vehicle.year}
              onChange={(value) => {
                formik.setFieldValue('vehicle.make', value);
                formik.setFieldValue('vehicle.model', '');
                formik.setFieldTouched('vehicle.model', false);
              }}
              onBlur={formik.handleBlur}
              options={vehicleCatalogMaker}
              optionsErrorCallback={handleOptionsRejectedCallback}
              error={formik.touched?.vehicle?.make && formik.errors?.vehicle?.make}
              inline
              searchable
              dataTest="blog-form-vehicle-make"
            />

            <LoadableSelect
              label="Model"
              id="vehicle.model"
              value={formik.values.vehicle.model}
              disabled={!formik.values.vehicle.year || !formik.values.vehicle.make}
              onChange={(value) => {
                formik.setFieldValue('vehicle.model', value);
              }}
              onBlur={formik.handleBlur}
              options={vehicleCatalogModel}
              optionsErrorCallback={handleOptionsRejectedCallback}
              error={formik.touched?.vehicle?.model && formik.errors?.vehicle?.model}
              inline
              searchable
              dataTest="blog-form-vehicle-model"
            />
          </div>

          <DealershipAccountField
            label="Dealership Account Id"
            id="dealershipAccount"
            value={formik.values.dealershipAccount}
            onChange={(dealershipAccount) => {
              formik
                .setFieldValue('dealershipAccount', dealershipAccount)
                .then(formik.validateField('dealershipAccount'))
                .then(() =>
                  // https://github.com/jaredpalmer/formik/issues/529#issuecomment-1462897295
                  setTimeout(
                    () => formik.setFieldTouched('dealershipAccount', !!dealershipAccount),
                    0
                  )
                );
            }}
            error={formik.touched?.dealershipAccount && formik.errors?.dealershipAccount?.id}
            maxLength={50}
            dataTest="blog-form-dealership-account-id"
            bottomMarginSizing={1}
          />

          <TextArea
            label="Content Topic"
            id="contentTopic"
            value={formik.values.contentTopic}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            required
            dataTest="blog-form-content-topic"
            error={formik.touched?.contentTopic && formik.errors?.contentTopic}
          />
          <div className="grid grid-rows-1 grid-cols-2 gap-6 whitespace-nowrap">
            <div className="row-start-1 row-end-2 col-start-1 col-end-2">
              <Input
                label="Word Count"
                value={formik.values.wordCount}
                onChange={({ target: { value } }) => {
                  formik.setFieldValue('wordCount', value.replace(/[^\d]/, ''));
                }}
                onPaste={(evt) => {
                  formik.setFieldValue('wordCount', digitsOnly(evt));
                }}
                onBlur={formik.handleBlur}
                error={formik.touched?.wordCount && formik.errors?.wordCount}
                id="wordCount"
                maxLength={4}
                required
                dataTest="blog-form-word-count"
                max="2500"
                min="100"
              />
            </div>
            <div className="row-start-1 row-end-2 col-start-2 col-end-3 flex justify-end">
              <Checkbox
                label="Check with Copyscape"
                value={formik.values.executeCopyscapeSearch}
                onChange={({ target: { checked } }) => {
                  formik.setFieldValue('executeCopyscapeSearch', checked);
                }}
                onBlur={formik.handleBlur}
                type="checkbox"
                error={
                  formik.touched?.executeCopyscapeSearch && formik.errors?.executeCopyscapeSearch
                }
                id="copyscape"
                dataTest="blog-form-copyscape"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="w-3/12 max-h-max min-w-[347px] grow bg-white overflow-hidden">
        <div className="bg-gradient-to-r block h-[2px] from-gradient-blue-purple-300 to-gradient-blue-purple-200" />
        <div className="p-6 -mb-8">
          <TextArea
            label="Target Audience or Market"
            id="targetAudience"
            value={formik.values.targetAudience}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-target-audience"
            error={formik.touched?.targetAudience && formik.errors?.targetAudience}
          />
          <TextArea
            label="Geos"
            id="geos"
            value={formik.values.geos}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-geos"
            error={formik.touched?.geos && formik.errors?.geos}
          />
          <InputText
            label="Content Example 1"
            id="contentOne.url"
            value={formik.values.contentOne.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-content-one-url"
            error={formik.touched?.contentOne?.url && formik.errors?.contentOne?.url}
            placeholder="ex. https://www.dealer.com/"
          />
          <InputText
            id="contentOne.writeStyle"
            value={formik.values.contentOne.writeStyle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-content-one-write-style"
            error={formik.touched?.contentOne?.writeStyle && formik.errors?.contentOne?.writeStyle}
            placeholder="What to take from example ex. tone, style, etc."
          />
          <InputText
            label="Content Example 2"
            id="contentTwo.url"
            value={formik.values.contentTwo.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-content-two-url"
            error={formik.touched?.contentTwo?.url && formik.errors?.contentTwo?.url}
            placeholder="ex. https://www.dealer.com/"
          />
          <InputText
            id="contentTwo.writeStyle"
            value={formik.values.contentTwo.writeStyle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-content-two-write-style"
            error={formik.touched?.contentTwo?.writeStyle && formik.errors?.contentTwo?.writeStyle}
            placeholder="What to take from example ex. tone, style, etc."
          />
        </div>
      </div>
      <div className="w-3/12 max-h-max min-w-[347px] grow bg-white overflow-hidden">
        <div className="bg-gradient-to-r block h-[2px] from-gradient-blue-purple-200 to-gradient-blue-purple-100" />
        <div className="p-6 -mb-8">
          <TextArea
            label="Additional Information"
            id="additionalInformation"
            value={formik.values.additionalInformation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-additional-information"
            error={formik.touched?.additionalInformation && formik.errors?.additionalInformation}
          />
          <TextArea
            label="Negative Prompt"
            id="negativePrompt"
            value={formik.values.negativePrompt}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-negative-prompt"
            error={formik.touched?.negativePrompt && formik.errors?.negativePrompt}
          />
          <InputText
            label="Link 1"
            id="linkOne.url"
            value={formik.values.linkOne.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-link-one-url"
            error={formik.touched?.linkOne?.url && formik.errors?.linkOne?.url}
            placeholder="ex. https://www.dealer.com/"
          />
          <InputText
            id="linkOne.description"
            value={formik.values.linkOne.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-link-one-description"
            error={formik.touched?.linkOne?.description && formik.errors?.linkOne?.description}
            placeholder="Add link description"
          />
          <InputText
            label="Link 2"
            id="linkTwo.url"
            value={formik.values.linkTwo.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-link-two-url"
            error={formik.touched?.linkTwo?.url && formik.errors?.linkTwo?.url}
            placeholder="ex. https://www.dealer.com/"
          />
          <InputText
            id="linkTwo.description"
            value={formik.values.linkTwo.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="blog-form-link-two-description"
            error={formik.touched?.linkTwo?.description && formik.errors?.linkTwo?.description}
            placeholder="Add link description"
          />
        </div>
      </div>
      <div className="w-screen overflow-hidden p-4 flex justify-center gap-8">
        <Button
          type="submit"
          name="generate"
          dataTest="blog-form-generate"
          className="flex justify-center items-center shadow-sm rounded-sm bg-buttonGradient py-4 px-16 text-white font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={formik.handleSubmit}
          icon={<IconAutoFixHigh className="mr-2" />}
          label="Generate"
          disabled={formik.isSubmitting}
        />
        <Button
          type="button"
          name="reset"
          dataTest="blog-form-reset"
          className="flex justify-center bg-transparent py-4 text-purple-200 font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={formik.resetForm}
          label="Reset"
        />
      </div>
    </div>
  );
};

BlogForm.propTypes = {
  contentType: PropTypes.string.isRequired,
  handleContentType: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default BlogForm;
